/**
 * useVendorTranslation Hook
 *
 * Custom hook for vendor module translations
 * Provides type-safe access to vendor namespace translations
 *
 * Usage:
 * ```tsx
 * import { useVendorTranslation } from '@/hooks/useVendorTranslation';
 *
 * const MyComponent = () => {
 *   const { t } = useVendorTranslation();
 *   return <h1>{t('vendor.list.title')}</h1>;
 * };
 * ```
 */

import { useTranslation } from 'react-i18next';

/**
 * Hook for accessing vendor module translations
 *
 * @returns Translation functions and utilities scoped to vendors namespace
 */
export const useVendorTranslation = () => {
  // Use 'vendors' namespace to access vendor-specific translations
  const { t, i18n } = useTranslation(['vendors', 'common']);

  return {
    /**
     * Translation function for vendor module
     * @param key - Translation key (e.g., 'vendor.list.title')
     * @param options - Optional interpolation values
     */
    t,

    /**
     * i18next instance for advanced usage
     */
    i18n,

    /**
     * Current language code
     */
    language: i18n.language,

    /**
     * Change language
     * @param lang - Language code ('en', 'zh')
     */
    changeLanguage: (lang: string) => i18n.changeLanguage(lang),
  };
};

/**
 * Type-safe vendor translation keys (for IDE autocomplete)
 * Add more specific types as needed
 */
export type VendorTranslationKey =
  | 'vendor.list.title'
  | 'vendor.list.create'
  | 'vendor.fields.vendorId'
  | 'vendor.fields.vendorName'
  | 'vendor.actions.edit'
  | 'vendor.actions.delete'
  | 'vendor.status.active'
  | 'vendor.status.inactive'
  | string; // Allow any string for flexibility

/**
 * Default export
 */
export default useVendorTranslation;
